import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { IconSparkles, IconMessageCircle } from '@tabler/icons-react';
import { openWidget } from 'actions/main';
import {
  handleFreeFormInput,
  setFreeFormQuery,
} from 'actions/workflows-v2/workflowsV2';
import { FloatingArticleSuggestions } from 'components/FloatingArticleSuggestions';
import IntentFormWrapper from 'components/FormWrapper/IntentFormWrapper';
import ProactiveBubbles from 'components/ProactiveBubbles';
import { TextInput } from 'components/TextInput/TextInput';
import { DEFAULT_MARGIN } from 'embed/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import {
  selectIsPrivacyConsented,
  selectLegibleElementColor,
  selectPlacement,
  selectWidgetArticleConfig,
} from 'reducers/config';
import { selectWindowDimensions } from 'reducers/main';
import {
  selectFreeFormInputPlaceholder,
  selectFreeFormQuery,
  selectIsConversationActive,
  selectIsFloatingBarFocused,
  selectIsProactiveChatVisible,
  selectFloatingBarSearchLoading,
  setActionToExecAfterPrivacyConsented,
  setIsFloatingBarFocused,
  setShouldFocusTextInput,
  selectIsFloatingBarExpanded,
} from 'reducers/workflows';
import { postIframeResizeEvent } from 'utils/postIframeResizeEvent';

export const FloatingBar = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const freeFormQuery = useAppSelector(selectFreeFormQuery);
  const placeholder = useAppSelector(selectFreeFormInputPlaceholder);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isPrivacyConsented = useAppSelector(selectIsPrivacyConsented);
  const isFloatingBarFocused = useAppSelector(selectIsFloatingBarFocused);
  const isFloatingBarExpanded = useAppSelector(selectIsFloatingBarExpanded);
  const isConversationActive = useAppSelector(selectIsConversationActive);
  const isProactiveChatVisible = useAppSelector(selectIsProactiveChatVisible);
  const placement = useAppSelector(selectPlacement);
  const widgetArticleSearchConfig = useAppSelector(selectWidgetArticleConfig);
  const isFloatingBarSearchLoading = useAppSelector(
    selectFloatingBarSearchLoading,
  );
  const windowDimensions = useAppSelector(selectWindowDimensions);

  React.useEffect(() => {
    /**
     * without this the iframe will resize its width after the floating bar appears
     *
     * we also need to resize whenever the window resizes because of the wrapping of intent buttons
     */
    postIframeResizeEvent();
  }, [isFloatingBarFocused, windowDimensions.innerWidth]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFreeFormQuery(e.target.value));
  };

  const onSendMessage = (event: React.SyntheticEvent) => {
    event.preventDefault();

    dispatch(setShouldFocusTextInput(true));

    if (isConversationActive) {
      dispatch(openWidget());
      return;
    }

    if (!isPrivacyConsented) {
      dispatch(openWidget());
      dispatch(setActionToExecAfterPrivacyConsented('handleFreeFormInput'));
      return;
    }

    dispatch(openWidget());
    dispatch(handleFreeFormInput());
  };

  const EndpointAdornment = React.useMemo(() => {
    const onClick = () => {
      if (isConversationActive) {
        dispatch(openWidget());
      }
    };

    if (!isConversationActive) {
      return undefined;
    }

    return (
      <InputAdornment position='end'>
        <IconButton
          aria-label='Open chat'
          onClick={onClick}
          sx={{
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              color: 'transparent',
              outline: '2px solid var(--legible-element-color)',
            },
            '&:hover': {
              backgroundColor: theme.palette.colors.slate[200],
            },
            backgroundColor: theme.palette.colors.slate[200],
            border: `1px solid ${theme.palette.colors.slate[300]}`,
            borderRadius: '20px',
            padding: '8px 16px',
          }}
        >
          <IconMessageCircle color={legibleElementColor} size={24} />
        </IconButton>
      </InputAdornment>
    );
  }, [
    isConversationActive,
    theme.palette.colors.slate,
    legibleElementColor,
    dispatch,
  ]);

  // width of the floating bar
  const expandedWidth = `calc(100% - ${DEFAULT_MARGIN}px)`;
  const unexpandedWidth = `min(${expandedWidth}, 90%)`;
  const width = isFloatingBarExpanded ? expandedWidth : unexpandedWidth;

  return (
    <Box
      onMouseEnter={() => {
        dispatch(setIsFloatingBarFocused(true));
      }}
      onMouseLeave={() => {
        dispatch(setIsFloatingBarFocused(false));
      }}
      sx={{
        display: 'flex',
        flexDirection: placement === 'bottom' ? 'column' : 'column-reverse',
        marginLeft: 'auto',
        marginRight: 'auto',
        transition:
          'width 0.2s cubic-bezier(0.4, 0, 0.6, 1), height 0.2s cubic-bezier(0.4, 0, 0.6, 1), transform 0.2s ease-in-out',
        width: width,
      }}
    >
      {/* intent options */}
      {isProactiveChatVisible && (
        <ProactiveBubbles
          className='proactive-bubbles-container'
          key='proactive-bubbles'
          useResizeIds={false}
        />
      )}
      {/* floating bar article suggestions */}
      {widgetArticleSearchConfig.enabled && <FloatingArticleSuggestions />}
      {/* floating bar input */}
      <IntentFormWrapper
        background={false}
        formTitle=''
        isDisabled={false}
        isForm
        isLastStep
        key='floating-bar-input'
        onSubmit={onSendMessage}
        stepIndex={-1}
      >
        <TextInput
          alwaysFocusedBorder
          dir='auto'
          disableSendMessage={!freeFormQuery.trim()}
          EndAdornment={EndpointAdornment}
          InputProps={{
            inputProps: {
              'aria-label': 'Type a message to send',
              className: 'forethought-scrollbar',
              'data-testid': 'floating-bar-input',
              onBlur: event => {
                const isFocusingProactiveBubbles = event.relatedTarget?.closest(
                  '.proactive-bubbles-container',
                );

                // Do not hide proactive bubbles if focusing to them:
                if (isFocusingProactiveBubbles) {
                  return;
                }

                /**
                 * setTimeout ensures if the blur is from clicking send or clicking
                 * on an intent button that those still fire.
                 */
                setTimeout(() => {
                  dispatch(setIsFloatingBarFocused(false));
                }, 500);
              },
              onFocus: () => {
                dispatch(setIsFloatingBarFocused(true));
              },
            },
            startAdornment: (
              <InputAdornment position='start'>
                {isFloatingBarSearchLoading ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: legibleElementColor }}
                  />
                ) : (
                  <IconSparkles color={legibleElementColor} size={24} />
                )}
              </InputAdornment>
            ),
            style: {
              backgroundColor: theme.palette.colors.white,
              boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.25)',
            },
          }}
          onChange={onChange}
          onSendMessage={onSendMessage}
          placeholder={placeholder}
          value={freeFormQuery}
          visuallyHiddenSendButton={!freeFormQuery.trim()}
        />
      </IntentFormWrapper>
    </Box>
  );
};
